import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { useToast } from "../../hooks/use-toast";
import { 
  Crown, 
  TrendingUp, 
  Users, 
  DollarSign, 
  Award,
  CheckCircle,
  Target,
  Briefcase,
  Globe,
  Star,
  Zap
} from "lucide-react";
import PaymentForm from "../payment/PaymentForm";

const ResellerUpgrade = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showPayment, setShowPayment] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const resellerBenefits = [
    {
      icon: <DollarSign className="h-6 w-6 text-green-600" />,
      title: "Wholesale Pricing",
      description: "Save 25-40% on all products with exclusive reseller rates",
      highlight: "Up to 40% savings"
    },
    {
      icon: <TrendingUp className="h-6 w-6 text-blue-600" />,
      title: "Multi-Level Commissions",
      description: "Earn from your direct sales and your team's performance",
      highlight: "25% direct commission"
    },
    {
      icon: <Users className="h-6 w-6 text-purple-600" />,
      title: "Team Building Rewards",
      description: "Build your network and earn leadership bonuses",
      highlight: "Unlimited depth"
    },
    {
      icon: <Briefcase className="h-6 w-6 text-orange-600" />,
      title: "Marketing Materials",
      description: "Professional marketing kit with branded materials",
      highlight: "Custom branding"
    },
    {
      icon: <Globe className="h-6 w-6 text-indigo-600" />,
      title: "Social Media Tools",
      description: "Automated posting and social media integration",
      highlight: "Auto-posting"
    },
    {
      icon: <Award className="h-6 w-6 text-yellow-600" />,
      title: "Recognition Program",
      description: "Achievement levels with increasing rewards",
      highlight: "Bronze to Platinum"
    }
  ];

  const commissionStructure = [
    { level: "Level 1 (Direct Sales)", rate: "25%", description: "Your personal sales commission" },
    { level: "Level 2 (Your Team)", rate: "10%", description: "Commission from your direct recruits" },
    { level: "Level 3 (Team's Team)", rate: "5%", description: "Commission from second-tier team" },
    { level: "Level 4 (Third Tier)", rate: "2%", description: "Commission from third-tier team" },
    { level: "Level 5 (Fourth Tier)", rate: "1%", description: "Commission from fourth-tier team" }
  ];

  const achievementLevels = [
    {
      level: "Bronze",
      requirement: "$0 - $1,000/month",
      benefits: "Basic reseller benefits",
      color: "text-orange-600 bg-orange-100"
    },
    {
      level: "Silver",
      requirement: "$1,001 - $5,000/month",
      benefits: "15% commission boost + marketing bonus",
      color: "text-gray-600 bg-gray-100"
    },
    {
      level: "Gold",
      requirement: "$5,001 - $15,000/month",
      benefits: "20% commission boost + leadership bonus",
      color: "text-yellow-600 bg-yellow-100"
    },
    {
      level: "Platinum",
      requirement: "$15,001+/month",
      benefits: "25% commission boost + executive rewards",
      color: "text-purple-600 bg-purple-100"
    }
  ];

  const handleUpgradeRequest = () => {
    if (!agreedToTerms) {
      toast({
        title: "Agreement Required",
        description: "Please agree to the reseller terms and conditions",
        variant: "destructive"
      });
      return;
    }
    setShowPayment(true);
  };

  const handlePaymentSuccess = (paymentResult) => {
    toast({
      title: "Welcome to the Reseller Program!",
      description: "Your account has been upgraded. You now have access to wholesale pricing and commissions."
    });
    
    // Update user account status in localStorage (mock)
    const customerAuth = localStorage.getItem('customerAuth');
    if (customerAuth) {
      const userData = JSON.parse(customerAuth);
      userData.accountType = 'reseller';
      userData.resellerLevel = 'Bronze';
      userData.upgradeDate = new Date().toISOString();
      localStorage.setItem('customerAuth', JSON.stringify(userData));
    }
    
    setTimeout(() => {
      navigate('/dashboard');
    }, 2000);
  };

  if (showPayment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <Button 
              variant="ghost" 
              onClick={() => setShowPayment(false)}
              className="mb-4"
            >
              ← Back to Upgrade Details
            </Button>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Complete Your Reseller Upgrade</h1>
            <p className="text-gray-600">Secure payment processing for your annual reseller membership</p>
          </div>

          <PaymentForm
            orderData={{
              type: "reseller-upgrade",
              description: "Annual Reseller Program Membership",
              amount: 250.00,
              features: [
                "Wholesale pricing access",
                "Multi-level commission structure", 
                "Marketing materials and tools",
                "Priority customer support",
                "Social media integration",
                "Achievement recognition program"
              ]
            }}
            onPaymentSuccess={handlePaymentSuccess}
            onCancel={() => setShowPayment(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-emerald-500 to-blue-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <Crown className="h-5 w-5 text-yellow-300" />
              <span className="text-sm font-medium">Exclusive Reseller Program</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Become a CardMaster Pro Reseller
            </h1>
            <p className="text-xl text-emerald-100 mb-8 max-w-3xl mx-auto">
              Join our exclusive reseller network and earn significant commissions while building your own business with our proven MLM system.
            </p>
            
            <div className="flex items-center justify-center space-x-8 text-center">
              <div>
                <div className="text-3xl font-bold">$250</div>
                <div className="text-emerald-200">Annual Investment</div>
              </div>
              <div className="w-px h-12 bg-white/30"></div>
              <div>
                <div className="text-3xl font-bold">25%</div>
                <div className="text-emerald-200">Direct Commission</div>
              </div>
              <div className="w-px h-12 bg-white/30"></div>
              <div>
                <div className="text-3xl font-bold">5 Levels</div>
                <div className="text-emerald-200">MLM Depth</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Benefits Grid */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Reseller Program Benefits
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {resellerBenefits.map((benefit, index) => (
              <Card key={index} className="hover:shadow-lg transition-all duration-300 border-0 bg-gradient-to-br from-white to-gray-50/50">
                <CardContent className="p-6">
                  <div className="mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 mb-3">
                    {benefit.description}
                  </p>
                  <Badge className="bg-emerald-100 text-emerald-800">
                    {benefit.highlight}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Commission Structure */}
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <TrendingUp className="h-6 w-6 text-green-600" />
                <span>Multi-Level Commission Structure</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {commissionStructure.map((level, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h4 className="font-semibold text-gray-900">{level.level}</h4>
                      <p className="text-sm text-gray-600">{level.description}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-green-600">{level.rate}</div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 p-4 bg-green-50 rounded-lg">
                <h4 className="font-semibold text-green-900 mb-2">Example Earning Potential</h4>
                <p className="text-sm text-green-800">
                  With just 5 direct sales ($52.97 each) and a small team, you could earn $350-500+ monthly. 
                  Top performers earn $5,000-15,000+ per month!
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Award className="h-6 w-6 text-yellow-600" />
                <span>Achievement Levels</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {achievementLevels.map((achievement, index) => (
                  <div key={index} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <Badge className={achievement.color}>
                        {achievement.level}
                      </Badge>
                      <span className="text-sm font-medium text-gray-600">
                        {achievement.requirement}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{achievement.benefits}</p>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 p-4 bg-purple-50 rounded-lg">
                <h4 className="font-semibold text-purple-900 mb-2">Progression Rewards</h4>
                <p className="text-sm text-purple-800">
                  Each level unlocks additional commission bonuses, exclusive marketing materials, 
                  and priority support to help grow your business.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Upgrade CTA */}
        <Card className="bg-gradient-to-r from-emerald-500 to-blue-600 text-white">
          <CardContent className="p-8">
            <div className="text-center">
              <Crown className="h-16 w-16 text-yellow-300 mx-auto mb-6" />
              <h2 className="text-3xl font-bold mb-4">
                Ready to Start Your Reseller Journey?
              </h2>
              <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
                Join hundreds of successful resellers who are building profitable businesses with CardMaster Pro's proven system.
              </p>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-8 max-w-2xl mx-auto">
                <h3 className="text-xl font-semibold mb-4">What's Included in Your $250 Investment:</h3>
                <div className="grid md:grid-cols-2 gap-3 text-left">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-300" />
                    <span className="text-sm">Wholesale pricing access</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-300" />
                    <span className="text-sm">Multi-level commission system</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-300" />
                    <span className="text-sm">Professional marketing materials</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-300" />
                    <span className="text-sm">Social media automation tools</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-300" />
                    <span className="text-sm">Priority customer support</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-300" />
                    <span className="text-sm">Achievement recognition program</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center space-x-3 mb-6">
                <Checkbox
                  id="terms"
                  checked={agreedToTerms}
                  onCheckedChange={setAgreedToTerms}
                  className="bg-white/20 border-white/40"
                />
                <label htmlFor="terms" className="text-sm">
                  I agree to the{" "}
                  <a href="/reseller-terms" className="underline hover:text-yellow-300">
                    Reseller Terms & Conditions
                  </a>{" "}
                  and{" "}
                  <a href="/mlm-agreement" className="underline hover:text-yellow-300">
                    MLM Agreement
                  </a>
                </label>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button
                  onClick={handleUpgradeRequest}
                  size="lg"
                  className="bg-white text-emerald-600 hover:bg-gray-50 px-8 py-4 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <Zap className="mr-2 h-5 w-5" />
                  Upgrade to Reseller - $250/year
                </Button>
                
                <Button
                  variant="outline"
                  size="lg"
                  className="border-white text-white hover:bg-white/10 px-8 py-4 rounded-lg font-semibold text-lg"
                  onClick={() => navigate('/dashboard')}
                >
                  Maybe Later
                </Button>
              </div>

              <p className="text-sm text-emerald-200 mt-4">
                30-day money-back guarantee • Cancel anytime • Full support included
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResellerUpgrade;